import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import api from "src/store/features/api";
import { useAppDispatch } from "src/store/store";
import {
  resetWithdrawalThunk,
  setWalletAddress,
  setDisplayWalletAddress,
  setCryptoWithdrawalStage,
} from "src/store/features/withdrawal.slice";
import { getCopy, CopyScope, Entity } from "src/components/transfers/copy";
import { useIsMobile } from "src/utils/hooks/useIsMobile";

export interface TrThirdPartyBlockModalProps {
  open: boolean;
  scope?: CopyScope;
  /** Called when the user picks "Use a different address". */
  onUseDifferentAddress?: () => void;
  /** Called when the user picks "Cancel". Defaults to `resetWithdrawalThunk`. */
  onCancel?: () => void;
}

/**
 * D5 — Third-party block modal. Renders a centered, constrained MUI Dialog on
 * desktop and a full-screen Dialog on mobile, with copy keyed by
 * `(entity, scope)` from `apps/frontend/src/components/transfers/copy.ts`.
 *
 * Two CTAs:
 *   - "Use a different address" — resets the address selection in the
 *     withdrawal slice and returns to InputDetails.
 *   - "Cancel" — calls `resetWithdrawalThunk` (full withdrawal reset).
 *
 * Used inline by `<AddressTrOwnership />` and `<CryptoWithdrawalTrBackfill />`.
 */
const TrThirdPartyBlockModal: React.FC<TrThirdPartyBlockModalProps> = ({
  open,
  scope = "thirdPartyBlock",
  onUseDifferentAddress,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const { data: userRes } = api.useGetUserDatav2Query();

  // Map subsidiary → entity; default to AU since the copy entries are nearly
  // identical and AUSTRAC is the stricter regulator.
  const subsidiary = userRes?.subsidiary ?? "";
  const entity: Entity =
    subsidiary === "6" || subsidiary === "US" || subsidiary === "CnB Limited" ? "US" : "AU";

  const entry = getCopy(entity, scope);

  const handleUseDifferent = () => {
    if (onUseDifferentAddress) {
      onUseDifferentAddress();
      return;
    }
    // Default behaviour: clear the selected address and return to InputDetails.
    dispatch(setWalletAddress(""));
    dispatch(setDisplayWalletAddress(""));
    dispatch(setCryptoWithdrawalStage("InputDetails"));
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    dispatch(resetWithdrawalThunk());
  };

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      aria-labelledby="tr-third-party-block-title">
      <DialogTitle id="tr-third-party-block-title">{entry.title}</DialogTitle>
      <DialogContent>
        <DialogContentText data-testid="tr-third-party-block-body">
          {entry.body}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} data-testid="tr-third-party-block-cancel">
          {entry.secondaryCta ?? "Cancel"}
        </Button>
        <Button
          onClick={handleUseDifferent}
          variant="contained"
          data-testid="tr-third-party-block-use-different">
          {entry.primaryCta ?? "Use a different address"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrThirdPartyBlockModal;
